import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAppState } from '../hooks/useAppState';
import { getConfig } from '../services/configManager';
import apiService from '../services/api';
import type { OrderTicketVoidStatus } from '../types/api';
import BouncingDots from '../components/BouncingDots';

const PENDING_MESSAGES = [
  'Processing your request',
  'Establishing connection',
  'Please wait...',
  'Almost there...',
];

const MESSAGE_CYCLE_MS = 2500;
const SUCCESS_DISPLAY_MS = 1000;

type PageStatus = 'pending' | 'success' | 'error';

const TicketVoidWaitingPage: React.FC = () => {
  const { state, setStep } = useAppState();
  const { orderResponse, selectedFlow } = state;

  const [status, setStatus] = useState<PageStatus>('pending');
  const [messageIndex, setMessageIndex] = useState(0);
  const [isFadingOut, setIsFadingOut] = useState(false);

  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deadlineRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const messageCycleRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMounted = useRef(true);

  const config = getConfig();
  const maxMs = (config.VOID_WAITING?.MaxSeconds ?? 20) * 1000;
  const pollMs = config.VOID_WAITING?.PollIntervalMs ?? 3000;

  const orderId = orderResponse?.order?.id;

  const clearTimers = useCallback(() => {
    if (pollingRef.current) clearTimeout(pollingRef.current);
    if (deadlineRef.current) clearTimeout(deadlineRef.current);
    if (messageCycleRef.current) clearInterval(messageCycleRef.current);
  }, []);

  const goToThankYou = useCallback(() => {
    const target = selectedFlow === 'member' ? 'member-thank-you' : 'guest-thank-you';
    setStep(target);
  }, [selectedFlow, setStep]);

  const handleSuccess = useCallback(() => {
    clearTimers();
    setStatus('success');
    setTimeout(() => {
      if (isMounted.current) goToThankYou();
    }, SUCCESS_DISPLAY_MS);
  }, [clearTimers, goToThankYou]);

  const handleError = useCallback(() => {
    clearTimers();
    setStatus('error');
  }, [clearTimers]);

  const poll = useCallback(async () => {
    if (!orderId || !isMounted.current) return;

    try {
      const res = await apiService.getOrderVoidStatus(orderId);
      if (!isMounted.current) return;

      const apiStatus: OrderTicketVoidStatus = res.status;

      if (apiStatus === 'success') {
        handleSuccess();
      } else if (apiStatus === 'error') {
        handleError();
      } else {
        pollingRef.current = setTimeout(poll, pollMs);
      }
    } catch {
      if (isMounted.current) handleError();
    }
  }, [orderId, pollMs, handleSuccess, handleError]);

  useEffect(() => {
    isMounted.current = true;

    poll();

    deadlineRef.current = setTimeout(() => {
      if (isMounted.current && status === 'pending') handleError();
    }, maxMs);

    messageCycleRef.current = setInterval(() => {
      setIsFadingOut(true);
      setTimeout(() => {
        setMessageIndex(prev => (prev + 1) % PENDING_MESSAGES.length);
        setIsFadingOut(false);
      }, 400);
    }, MESSAGE_CYCLE_MS);

    return () => {
      isMounted.current = false;
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetry = () => {
    setStatus('pending');
    setMessageIndex(0);
    setIsFadingOut(false);

    poll();

    deadlineRef.current = setTimeout(() => {
      if (isMounted.current && status === 'pending') handleError();
    }, maxMs);

    messageCycleRef.current = setInterval(() => {
      setIsFadingOut(true);
      setTimeout(() => {
        setMessageIndex(prev => (prev + 1) % PENDING_MESSAGES.length);
        setIsFadingOut(false);
      }, 400);
    }, MESSAGE_CYCLE_MS);
  };

  const handleBackToHome = () => {
    setStep('home');
  };

  return (
    <div className="flex-1 flex flex-col p-5">
      <div className="flex-1 flex items-center justify-center">
        <div className="container-card w-full max-w-sm text-center">
          <h1 className="text-title font-extrabold text-black text-center mb-8">
            Sending Information
          </h1>

          {/* Status icon / loader area */}
          <div className="flex justify-center mb-6">
            {status === 'pending' && <BouncingDots />}

            {status === 'success' && (
              <div className="animate-fade-in">
                <svg className="w-16 h-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            )}

            {status === 'error' && (
              <div className="animate-fade-in">
                <svg className="w-16 h-16 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            )}
          </div>

          {/* Status message */}
          <div className="min-h-[60px] flex items-center justify-center mb-8">
            {status === 'pending' && (
              <p
                className={`text-md text-gray-600 transition-opacity duration-400 ${
                  isFadingOut ? 'animate-fade-out' : 'animate-fade-in'
                }`}
              >
                {PENDING_MESSAGES[messageIndex]}
              </p>
            )}

            {status === 'success' && (
              <p className="text-md font-semibold text-green-600 animate-fade-in">Done!</p>
            )}

            {status === 'error' && (
              <p className="text-md text-red-600 animate-fade-in">
                We couldn't complete your Scan to Pay request right now. <br />
                No charges were made to your card. <br />
                Please try again in a few minutes or pay at a payment station.                
              </p>
            )}
          </div>

          {/* Action buttons (error / timeout only) */}
          {status === 'error' && (
            <div className="flex flex-col gap-4 animate-fade-in">
              <button onClick={handleRetry} className="btn-primary w-full">
                Retry
              </button>
              <button
                onClick={handleBackToHome}
                className="w-full px-6 py-3 text-md font-semibold text-black bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
              >
                Back to Home
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketVoidWaitingPage;
